import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ReactComponent as CheckCircle } from '../images/icons/check-circle.svg';
import { ReactComponent as ErrorCircle } from '../images/icons/error-circle.svg';
import { ReactComponent as InfoCircle } from '../images/icons/info-circle.svg';
import {
  colourGreen02,
  colourGreen03,
  colourGreen06,
  colourGreen08,
  colourGreen10,
  colourNeutral02,
  colourNeutral03,
  colourNeutral06,
  colourNeutral08,
  colourNeutral10,
  colourRed02,
  colourRed03,
  colourRed08,
  colourRed10,
  colourYellow02,
  colourYellow03,
  colourYellow05,
  colourYellow08,
  colourYellow10,
} from '../styling/colours';
import { fontSize05, fontWeightBold } from '../styling/fonts';
import { spacing02, spacing03, spacing04, spacing05, spacing06 } from '../styling/spacing';

type AlertType = 'info' | 'success' | 'warning' | 'error';

type Props = {
  type: AlertType;
  title?: React.ReactNode;
  body?: React.ReactNode;
  'data-testid'?: string;
  className?: string;
  small?: boolean;
};

export const Alert = (props: Props) => (
  <AlertContainer
    $type={props.type}
    $small={props.small}
    data-testid={props['data-testid']}
    className={props.className}
  >
    <AlertIconContainer $type={props.type} $small={props.small}>
      {props.type === 'info' && <InfoCircle />}
      {props.type === 'success' && <CheckCircle />}
      {props.type === 'warning' && <ErrorCircle />}
      {props.type === 'error' && <ErrorCircle />}
    </AlertIconContainer>
    <AlertContent>
      {props.title && <AlertTitle>{props.title}</AlertTitle>}
      {props.body && <AlertBody>{props.body}</AlertBody>}
    </AlertContent>
  </AlertContainer>
);

const AlertContainer = styled.div<{ $type: AlertType; $small?: boolean }>`
  border: solid 1px ${colourNeutral08};
  background-color: ${props =>
    (props.$type === 'success' && colourGreen10) ||
    (props.$type === 'warning' && colourYellow10) ||
    (props.$type === 'error' && colourRed10) ||
    colourNeutral10};
  color: ${props =>
    (props.$type === 'success' && colourGreen02) ||
    (props.$type === 'warning' && colourYellow02) ||
    (props.$type === 'error' && colourRed02) ||
    colourNeutral02};
  border-color: ${props =>
    (props.$type === 'success' && colourGreen06) ||
    (props.$type === 'warning' && colourYellow05) ||
    (props.$type === 'error' && colourRed08) ||
    colourNeutral06};
  ${props =>
    props.$small
      ? css`
          padding: ${spacing02} ${spacing03};
        `
      : css`
          padding: ${spacing04} ${spacing05};
        `};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const alertIconSize = spacing06;
const alertSmallIconSize = spacing05;

const AlertIconContainer = styled.div<{ $type: AlertType; $small?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${props => (props.$small ? alertSmallIconSize : alertIconSize)};
  width: ${props => (props.$small ? alertSmallIconSize : alertIconSize)};
  border-radius: 100%;
  margin-right: ${props => (props.$small ? spacing04 : spacing05)};
  background-color: ${props =>
    (props.$type === 'success' && colourGreen03) ||
    (props.$type === 'warning' && colourYellow03) ||
    (props.$type === 'error' && colourRed03) ||
    colourNeutral03};

  svg {
    height: ${props => (props.$small ? alertSmallIconSize : alertIconSize)};
    width: ${props => (props.$small ? alertSmallIconSize : alertIconSize)};
    stroke: none;
    fill: ${props =>
      (props.$type === 'success' && colourGreen08) ||
      (props.$type === 'warning' && colourYellow08) ||
      (props.$type === 'error' && colourRed08) ||
      colourNeutral08};
    color: ${props =>
      (props.$type === 'success' && colourGreen08) ||
      (props.$type === 'warning' && colourYellow08) ||
      (props.$type === 'error' && colourRed08) ||
      colourNeutral08};
  }
`;

const AlertContent = styled.div`
  flex: 1;
`;

const AlertTitle = styled.div`
  font-size: ${fontSize05};
  margin-bottom: ${spacing02};
  font-weight: ${fontWeightBold};
`;

const AlertBody = styled.div``;
